import { Box, Container, Grid, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Card,
  IconButton,
  Icons,
  MaterialPalette,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import { Auth } from 'aws-amplify';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { IMAGE_PATH } from '../../utilities/constants';
import { logError } from '../Shared/analytics';

const useStyles = makeStyles({
  box: {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    minHeight: '300px',
    zIndex: 2,
    '& .subtitle': {
      marginTop: 20,
    },
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    margin: 32,
    padding: 0,
  },
  logo: {
    marginBottom: '30px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  diffUserLink: {
    marginBottom: '16px',
  },
  linkContainer: {
    marginTop: '0px',
    '& .sentCodeTextSubtitle': {
      margin: '20px 0px 20px 0px',
    },
  },
  title: {
    textAlign: 'center',
    width: '100%',
  },
  button: {
    width: '190px',
    margin: '0 auto',
    marginTop: '50px',
    justifyContent: 'flex-start',
  },
  linkMargin: {
    marginTop: '20px',
  },
  emailLink: {
    textDecoration: 'none',
  },
  space: {
    marginTop: '20px',
  },
  passwordGrid: {
    marginTop: '10px',
  },
  resetPasswordGrid: {
    marginTop: '20px',
    '& #resetPassword': {
      borderRadius: '4px',
    },
    '& #resetPasswordDisabled': {
      borderRadius: '4px',
      backgroundColor: 'rgba(255, 255, 255, 0.48)',
    },
  },
  resetPasswordLabel: {
    color: MaterialPalette.common.black,
  },
  usernameTextGrid: {
    marginTop: '25px',
  },
  confirmUsernameButtonGrid: {
    marginTop: '36px',
    '& #confirmUsernameButton': {
      borderRadius: '4px',
    },
    '& #confirmUsernameButtonDisabled': {
      borderRadius: '4px',
      backgroundColor: 'rgba(255, 255, 255, 0.48)',
    },
  },
  usernameTextField: {
    textDecorationColor: 'white',
  },
  confirmUsernameLabel: {
    color: MaterialPalette.common.black,
  },
});

const ForgotPassword: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [requestedReset, setRequestedReset] = useState(false);
  const defaultEmailValue = localStorage.getItem('userName') || '';
  const [values, setValues] = useState<{
    email: string | null;
    validEmail: boolean;
    emailError: string | null;
  }>({
    email: defaultEmailValue,
    validEmail: true,
    emailError: null,
  });
  const [sendingRequest, setSendingRequest] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [codeError, setCodeError] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [dest, setDest] = useState('');
  const [policyMessage, setPolicyMessage] = useState('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value, name },
    } = event;
    setValues({
      ...values,
      [name]: value,
    });
    localStorage.setItem('userName', value);
  };

  const VerifyCode = (event: React.FocusEvent<HTMLInputElement>) => {
    setCodeError('');
    setPasswordError('');
    const value = event.target.value;
    setCode(value);
  };

  const handleResetPassword = async () => {
    localStorage.removeItem('userName');
    setSendingRequest(true);
    if (newPassword === confirmNewPassword) {
      try {
        await Auth.forgotPasswordSubmit(values.email as string, code, newPassword);
        history.push('/login');
      } catch (err) {
        setSendingRequest(false);
        const error = err as { code: string; message: string; name: string };
        if ('code' in error) {
          switch (error.code) {
            case 'CodeMismatchException':
              setCodeError('Invalid verification code. Please enter the recent verification code');
              return 'Invalid verification code. Please enter the recent verification code';
            case 'InvalidPasswordException':
              setPasswordError('Password not long enough');
              return 'Password not long enough';
            case 'LimitExceededException':
              setPasswordError('Attempt limit exceeded, please try after after 15 minute');
              return 'Attempt limit exceeded, please try after 15 minute';
            case 'UserNotFoundException':
              return 'Account not found.';
            case 'UsernameExistsException':
              return 'Account already Exists with same username.';
            case 'NotAuthorizedException':
              if ('message' in error && error.message === 'User is disabled.') {
                return 'Account disabled.';
              }
              if (
                'message' in error &&
                error.message ===
                  'Temporary password has expired and must be reset by an administrator.'
              ) {
                return 'Your temporary password has expired, please contact your Nutrien Representative for assistance.';
              }
              return 'The email or password is incorrect. Try again or contact IT.';
            case 'PasswordResetRequiredException':
              if ('message' in error && error.message === 'Password reset required for the user') {
                return 'Password Expired. Use the Forgot Password link below to reset it.';
              }
          }
        } else {
          return 'An unknown error occurred.';
        }
      }
    } else {
      setPasswordError('Password did not match');
    }
    setSendingRequest(false);
  };

  const handleGetConfirmationViewSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleResetPassword();
  };

  const handleNewPassword = (event: React.FocusEvent<HTMLInputElement>) => {
    setCodeError('');

    const value = event.target.value;
    setNewPassword(value);
    if (
      (value.length < 8 ||
        !RegExp(/.*[a-z].*/).test(value) ||
        !RegExp(/.*[A-Z].*/).test(value) ||
        !RegExp(/.*\d.*/).test(value) ||
        !RegExp(/[$&+,:;=?@#|'<>.^*()%!-]/).test(value)) &&
      value.length > 0
    ) {
      setPolicyMessage(
        'The password should have minimum 8 characters, in that please include one uppercase, one lowercase, one numeric and one special character.',
      );
    } else {
      setPolicyMessage('');
    }
  };

  const handleConfirmNewPassword = (event: React.FocusEvent<HTMLInputElement>) => {
    setCodeError('');
    setPasswordError('');
    const value = event.target.value;
    setConfirmNewPassword(value);
    if (newPassword !== value) {
      setPasswordError('Password did not match');
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseDownConfirmPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const validateForm = () => {
    let emailError = null;
    setValues({ ...values, emailError });

    //Note: Check for empty email field
    if (!values.email) {
      emailError = 'Username Required.';
    }

    setValues({ ...values, emailError });
    return emailError === null;
  };

  const handleForgotPasswordSubmit = async () => {
    setSendingRequest(true);
    const valid = validateForm();
    if (!valid) return setSendingRequest(false);
    try {
      const user = await Auth.forgotPassword(values.email as string);
      setDest(user.CodeDeliveryDetails.Destination);
      setSendingRequest(false);
      setRequestedReset(true);
    } catch (err) {
      const error = err as { code: string; message: string; name: string };
      logError(error, false, 'file: ForgotPassword.tsx ~ handleForgotPasswordSubmit ~ error');
      let emailError = 'An unknown error occurred.';
      if ('code' in { error } && 'message' in { error }) {
        if (
          error.code === 'NotAuthorizedException' &&
          error.message === 'User password cannot be reset in the current state.'
        )
          emailError = 'Error, please contact customer support.';
      }

      setSendingRequest(false);
      return setValues({
        ...values,
        emailError,
      });
    }
  };

  const handleGetRequestViewSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  return (
    <Grid container justifyContent="center" alignItems="center" md={12}>
      <Box className={classes.box}>
        <Card smoothShadow>
          <Container className={classes.container} maxWidth="xs">
            <img src={`${IMAGE_PATH}/nutrien_dark.svg`} />
            <Typography variant="h5">AIMS - INSPECTION APP </Typography>
            <Grid container>
              <Grid item xs={12}>
                {requestedReset ? (
                  <form onSubmit={handleGetConfirmationViewSubmit}>
                    <Grid
                      container
                      spacing={0}
                      className={classes.linkContainer}
                      justifyContent="center"
                    >
                      <Typography align="center" className="subtitle sentCodeTextSubtitle">
                        {`We have sent code to your email ${dest}.
                          Enter it below to reset your password.`}
                      </Typography>

                      <Grid item xs={12}>
                        <TextField
                          noMinHeight
                          size="small"
                          autoFocus
                          error={codeError !== ''}
                          errorText={codeError}
                          onChange={VerifyCode}
                          label="Verification Code"
                        />

                        <Grid className={classes.passwordGrid}>
                          <TextField
                            id="Password"
                            key="Password"
                            name="Password"
                            noMinHeight
                            label="New Password"
                            onChange={handleNewPassword}
                            error={policyMessage !== ''}
                            errorText={policyMessage || ' '}
                            type={showPassword ? 'text' : 'password'}
                            value={newPassword}
                            size="small"
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  tabIndex={-1}
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowPassword}
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                >
                                  {showPassword ? (
                                    <Icons.EyeFeather strokeWidth={2} />
                                  ) : (
                                    <Icons.EyeOffFeather strokeWidth={2} />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                        </Grid>
                        <Grid className={classes.passwordGrid}>
                          <TextField
                            id="Password"
                            key="Password"
                            name="Password"
                            noMinHeight
                            label="Confirm New Password"
                            onChange={handleConfirmNewPassword}
                            onFocus={handleConfirmNewPassword}
                            error={passwordError !== '' && confirmNewPassword !== ''}
                            errorText={passwordError}
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmNewPassword}
                            size="small"
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  tabIndex={-1}
                                  aria-label="toggle password visibility"
                                  onClick={handleClickShowConfirmPassword}
                                  onMouseDown={handleMouseDownConfirmPassword}
                                  edge="end"
                                >
                                  {showConfirmPassword ? (
                                    <Icons.EyeFeather strokeWidth={2} />
                                  ) : (
                                    <Icons.EyeOffFeather strokeWidth={2} />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                        </Grid>
                        <Grid className={classes.resetPasswordGrid}>
                          <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={
                              values.email === '' ||
                              newPassword === '' ||
                              confirmNewPassword === '' ||
                              code === '' ||
                              policyMessage !== '' ||
                              passwordError !== ''
                            }
                            onClick={handleResetPassword}
                            className={`${classes.diffUserLink}`}
                            id={`${
                              values.email === '' ||
                              newPassword === '' ||
                              confirmNewPassword === '' ||
                              code === '' ||
                              policyMessage !== '' ||
                              passwordError !== ''
                                ? 'resetPasswordDisabled'
                                : 'resetPassword'
                            }`}
                            isLoading={sendingRequest}
                            noMinHeight
                          >
                            <Typography fontWeight={700} className={classes.resetPasswordLabel}>
                              Reset Password
                            </Typography>
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </form>
                ) : (
                  <form onSubmit={handleGetRequestViewSubmit}>
                    <Grid item container xs={12} justifyContent="center" className={classes.space}>
                      <Typography align="center" className="subtitle">
                        Enter your Username below and we will send a code to reset your password
                      </Typography>
                    </Grid>

                    <Grid className={classes.usernameTextGrid}>
                      <TextField
                        className={classes.usernameTextField}
                        label="User name"
                        noMinHeight
                        placeholder="Enter username"
                        onChange={handleChange}
                        name="email"
                        size="small"
                        errorText={values.emailError !== null ? values.emailError : ''}
                        error={values.emailError !== null}
                        defaultValue={defaultEmailValue}
                      />
                    </Grid>

                    <Grid className={classes.confirmUsernameButtonGrid}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={!(defaultEmailValue?.length || values?.email)}
                        onClick={handleForgotPasswordSubmit}
                        className={classes.diffUserLink}
                        isLoading={sendingRequest}
                        noMinHeight
                        id={
                          !values?.email?.length
                            ? 'confirmUsernameButtonDisabled'
                            : 'confirmUsernameButton'
                        }
                      >
                        <Typography fontWeight={700} className={classes.confirmUsernameLabel}>
                          Confirm Username
                        </Typography>
                      </Button>
                    </Grid>
                  </form>
                )}
              </Grid>
            </Grid>
          </Container>
        </Card>
      </Box>
    </Grid>
  );
};

export default ForgotPassword;
